import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar,
  DialogContentText,
  Divider,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Warning,
} from '@mui/icons-material';
import { approvalService, PendingChange } from '../../services/approval.service';

interface PendingApprovalsSectionProps {
  entityType: string;
  title?: string;
  onApprovalComplete?: () => void;
}

const PendingApprovalsSection: React.FC<PendingApprovalsSectionProps> = ({
  entityType,
  title,
  onApprovalComplete,
}) => {
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedChange, setSelectedChange] = useState<PendingChange | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchPendingChanges();
  }, [entityType]);

  const fetchPendingChanges = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await approvalService.getPendingChanges({
        entity_type: entityType,
        per_page: 100,
      });
      setPendingChanges(response.data?.data || []);
    } catch (err: any) {
      setError('Failed to load pending approvals');
      console.error('Error fetching pending changes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (change: PendingChange) => {
    setSelectedChange(change);
    setShowApproveDialog(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedChange) return;

    try {
      setActionLoading(true);
      await approvalService.approve(selectedChange.id);
      setShowApproveDialog(false);
      setSnackbar({
        open: true,
        message: `${formatActionType(selectedChange.action_type)} approved successfully!`,
        severity: 'success',
      });
      await fetchPendingChanges();
      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to approve change';
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (change: PendingChange) => {
    setSelectedChange(change);
    setRejectReason('');
    setShowRejectDialog(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedChange) return;

    try {
      setActionLoading(true);
      await approvalService.reject(selectedChange.id, rejectReason);
      setShowRejectDialog(false);
      setSnackbar({
        open: true,
        message: `${formatActionType(selectedChange.action_type)} rejected successfully!`,
        severity: 'info',
      });
      await fetchPendingChanges();
      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to reject change';
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewDetails = (change: PendingChange) => {
    setSelectedChange(change);
    setShowDetailsDialog(true);
  };

  const getActionColor = (actionType: string) => {
    switch (actionType) {
      case 'create':
        return 'success';
      case 'update':
        return 'info';
      case 'delete':
        return 'error';
      case 'deactivate':
        return 'warning';
      case 'activate':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatActionType = (actionType: string) => {
    return actionType.charAt(0).toUpperCase() + actionType.slice(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const columns: GridColDef[] = [
    {
      field: 'action_type',
      headerName: 'Action',
      width: 130,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={formatActionType(params.value)}
          size="small"
          color={getActionColor(params.value)}
        />
      ),
    },
    {
      field: 'created_by',
      headerName: 'Requested By',
      width: 180,
      display: 'flex',
      valueGetter: (value, row) => row?.created_by?.name || 'Unknown',
    },
    {
      field: 'created_at',
      headerName: 'Date',
      width: 200,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => formatDate(params.value),
    },
    {
      field: 'details',
      headerName: 'Details',
      width: 100,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title="View details">
          <IconButton
            size="small"
            onClick={() => handleViewDetails(params.row as PendingChange)}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Approve">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleApproveClick(params.row as PendingChange)}
              disabled={actionLoading}
            >
              <CheckCircle />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reject">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleRejectClick(params.row as PendingChange)}
              disabled={actionLoading}
            >
              <Cancel />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">
          {title || 'Pending Approvals'}
        </Typography>
        <Chip
          label={pendingChanges.length}
          size="small"
          color={pendingChanges.length > 0 ? 'warning' : 'default'}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={pendingChanges}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50]}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        />
      </Box>

      {/* Approve Dialog */}
      <Dialog
        open={showApproveDialog}
        onClose={() => !actionLoading && setShowApproveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle color="success" />
          Approve Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to approve the following change:
          </DialogContentText>
          {selectedChange && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Action:</strong> {formatActionType(selectedChange.action_type)}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Requested by:</strong> {selectedChange.created_by?.name || 'Unknown'}
                </Typography>
                <Typography variant="subtitle2">
                  <strong>Date:</strong> {formatDate(selectedChange.created_at)}
                </Typography>
              </Alert>

              <Divider sx={{ my: 2 }} />

              {selectedChange.action_type === 'update' && selectedChange.current_data && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Changes to be applied:
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedChange.proposed_data, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}

              {(selectedChange.action_type === 'create' || selectedChange.action_type === 'activate' || selectedChange.action_type === 'deactivate') && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Data to be {selectedChange.action_type}d:
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedChange.proposed_data, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}

              {selectedChange.action_type === 'delete' && selectedChange.current_data && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="warning" icon={<Warning />}>
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Warning:</strong> This will permanently delete the following item:
                    </Typography>
                  </Alert>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', mt: 1, maxHeight: 200, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedChange.current_data, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}

              <Alert severity="warning" sx={{ mt: 2 }}>
                Once approved, this change will be applied immediately and cannot be undone.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowApproveDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleApproveConfirm}
            color="success"
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {actionLoading ? 'Approving...' : 'Approve Change'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onClose={() => !actionLoading && setShowRejectDialog(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel color="error" />
          Reject Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            You are about to reject the following change:
          </DialogContentText>
          {selectedChange && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Action:</strong> {formatActionType(selectedChange.action_type)}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Requested by:</strong> {selectedChange.created_by?.name || 'Unknown'}
              </Typography>
              <Typography variant="subtitle2">
                <strong>Date:</strong> {formatDate(selectedChange.created_at)}
              </Typography>
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason (Optional)"
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={actionLoading}
            placeholder="Provide a reason for rejecting this change..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowRejectDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <Cancel />}
          >
            {actionLoading ? 'Rejecting...' : 'Reject Change'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Change Details</DialogTitle>
        <DialogContent>
          {selectedChange && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Action: <Chip label={formatActionType(selectedChange.action_type)} size="small" />
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Requested by: {selectedChange.created_by?.name || 'Unknown'}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Date: {formatDate(selectedChange.created_at)}
              </Typography>

              {selectedChange.current_data && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Current Data:
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedChange.current_data, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}

              {selectedChange.proposed_data && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Proposed Changes:
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedChange.proposed_data, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default PendingApprovalsSection;
